import { PercentageChip } from './PercentageChip';
import {
  ArbitrageOrderBooks,
  InfoValuesType
} from 'src/Modules/Arbitrage/types';
import { ExchangeDetailCell } from './ExchangeDetailCell';
import { useCallback, useMemo } from 'react';
import {
  DataGrid,
  GridColDef,
  gridPageCountSelector,
  gridPageSelector,
  useGridApiContext,
  useGridSelector,
  GridPagination
} from '@mui/x-data-grid';
import { Box, IconButton, Typography, Tooltip } from '@mui/material';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import { CryptoItem } from '../CryptoItem';

interface Props {
  data?: ArbitrageOrderBooks[];
  isLoading?: boolean;
  setActiveCryptoDetail: (value: string, symbol?: string) => void;
}

function CustomPagination(props: any) {
  const apiRef = useGridApiContext();
  const page = useGridSelector(apiRef, gridPageSelector);
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);

  return (
    <Box display="flex" alignItems="center">
      <IconButton
        onClick={() => apiRef.current.setPage(0)}
        disabled={page === 0}
      >
        <FirstPageIcon />
      </IconButton>
      <GridPagination {...props} />
      <IconButton
        onClick={() => apiRef.current.setPage(pageCount - 1)}
        disabled={page === pageCount - 1}
      >
        <LastPageIcon />
      </IconButton>
    </Box>
  );
}

export const ArbitrageProfitTable = ({
  data,
  isLoading,
  setActiveCryptoDetail
}: Props) => {
  const columns: GridColDef<ArbitrageOrderBooks>[] = useMemo(
    () => [
      {
        headerName: 'Token',
        field: 'name',
        type: 'string',
        headerAlign: 'center',
        renderCell: ({ row }) => (
          <div style={{ display: 'flex' }}>
            <CryptoItem symbol={row.symbol} slug={row.slug} />
          </div>
        ),
        width: 120
      },
      {
        headerName: 'Max Profit',
        field: 'maxProfit',
        type: 'number',
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ row, value }) => {
          return (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Typography variant="body2">{value ?? '-'}</Typography>
              {row.startUsdAmount && (
                <Tooltip title="startUsdAmount">
                  <Typography color="primary" variant="caption">
                    ( {row.startUsdAmount} )
                  </Typography>
                </Tooltip>
              )}
            </div>
          );
        },
        width: 150
      },
      {
        headerName: 'Profit %',
        field: 'profitPercentage',
        type: 'number',
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ value }) => (
          <PercentageChip value={value?.toFixed(2) ?? '-'} />
        ),
        width: 150
      },
      {
        headerName: 'Last Calc',
        field: 'calculatedAt',
        type: 'number',
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ value }) => {
          if (!value) return <Typography variant="caption">-</Typography>;
          const seconds = Math.round((Date.now() - value) / 1000);
          const label = seconds < 60 ? `${seconds}s ago` : `${Math.round(seconds / 60)}m ago`;
          return (
            <Tooltip title={new Date(value).toLocaleTimeString()}>
              <Typography variant="caption">{label}</Typography>
            </Tooltip>
          );
        },
        width: 100
      },
      {
        headerName: 'Min 1',
        field: 'min1',
        width: 230,
        headerAlign: 'center',
        renderCell: ({ row }) => (
          <ExchangeDetailCell
            rowData={row}
            valueType={InfoValuesType.MIN}
            cellIndex={1}
          />
        )
      },
      {
        headerName: 'Max 1',
        field: 'max1',
        width: 230,
        headerAlign: 'center',
        renderCell: ({ row }) => (
          <ExchangeDetailCell
            rowData={row}
            valueType={InfoValuesType.MAX}
            cellIndex={1}
          />
        )
      },
      {
        headerName: 'Min 2',
        field: 'min2',
        width: 230,
        headerAlign: 'center',
        renderCell: ({ row }) => (
          <ExchangeDetailCell
            rowData={row}
            valueType={InfoValuesType.MIN}
            cellIndex={2}
          />
        )
      },
      {
        headerName: 'Min 3',
        field: 'min3',
        width: 230,
        headerAlign: 'center',
        renderCell: ({ row }) => (
          <ExchangeDetailCell
            rowData={row}
            valueType={InfoValuesType.MIN}
            cellIndex={3}
          />
        )
      },
      {
        headerName: 'Max 2',
        field: 'max2',
        width: 230,
        headerAlign: 'center',
        renderCell: ({ row }) => (
          <ExchangeDetailCell
            rowData={row}
            valueType={InfoValuesType.MAX}
            cellIndex={2}
          />
        )
      },
      {
        headerName: 'Max 3',
        field: 'max3',
        width: 230,
        headerAlign: 'center',
        renderCell: ({ row }) => (
          <ExchangeDetailCell
            rowData={row}
            valueType={InfoValuesType.MAX}
            cellIndex={3}
          />
        )
      }
    ],
    []
  );

  const onClickHandler = useCallback(
    ({ row, field }: { row: ArbitrageOrderBooks; field: string }) => {
      if (field !== 'name') {
        return;
      }

      setActiveCryptoDetail(row.id, row.symbol);
    },
    [setActiveCryptoDetail]
  );

  return (
    <Box height="85vh">
      <DataGrid
        style={{ border: 'none' }}
        getRowId={row => row.id}
        loading={isLoading}
        density="comfortable"
        onCellClick={({ row, field }) => onClickHandler({ row, field })}
        headerHeight={20}
        rowHeight={60}
        rowsPerPageOptions={[25, 50, 100]}
        rows={data ?? []}
        initialState={{
          pagination: {
            pageSize: 25
          }
        }}
        components={{
          Pagination: CustomPagination
        }}
        columns={columns}
      />
    </Box>
  );
};
